import React from "react";
import { useTranslation } from "react-i18next";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/index";
import { TProductType, TProductVendor, TProductUnit } from "redux/reducers/models/product";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import DefaultsTableLayout from "./DefaultsTableLayout";
import "./productLogisticsTable.scss";

interface ILogisticState {
  customerRequiredEnabled?: boolean;
  fulfillmentRequiredEnabled?: boolean;
  shippingRequiredEnabled?: boolean;
  customer_required?: boolean;
  shipping_required?: boolean;
  fulfillment_required?: boolean;
  productType?: string;
  vendor_id?: number;
  kitchen_location_id?: number;
  kitchen_location_2_id?: number;
  kitchen_location_3_id?: number;
  trackInventoryEnabled?: boolean;
  track_inventory?: boolean;
  product_unit_id?: number;
}

interface ILogisticsSearch {
  productTypeSearch: string;
  vendorSearch: string;
  kitchenLocationSearch?: string;
  kitchenLocation2Search?: string;
  kitchenLocation3Search?: string;
  productUnitSearch?: string;
}

interface IProductLogisticsTableProps {
  state: ILogisticState;
  setState: React.Dispatch<React.SetStateAction<ILogisticState>>;
  logistics: ILogisticsSearch;
  onSearch: (key: string, query: string) => void;
  productTypes: TProductType[];
  vendors: TProductVendor[];
  kitchenLocations: TKitchenLocation[];
  /** When true, all toggles in this table are disabled. */
  togglesDisabled?: boolean;
  productUnits: TProductUnit[];
}

export default function ProductLogisticsTable(props: IProductLogisticsTableProps) {
  const { t } = useTranslation();
  const { state, setState, logistics, onSearch, productTypes, vendors, kitchenLocations, togglesDisabled, productUnits } = props;

  return (
    <DefaultsTableLayout tooltipId="product-logistics-default-value-tooltip">
      {/* Product Type */}
      <tr className="product-logistics-defaults-table-row">
        <td>{t("Product type")}</td>

        <td>
          <Select
            placeholder={t("Search product types...")}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("productTypeSearch", query)}
            onChange={(value: string) => setState(prev => ({ ...prev, productType: value }))}
            searchValue={logistics?.productTypeSearch || ""}
            showSearch // filters?.productTypeEnabled
            showDropDownOnFocus
            hideDropdown
            allowClear
          >
            {productTypes
              ?.filter(val => val?.title?.toLowerCase().includes(logistics.productTypeSearch?.toLowerCase()))
              ?.map(type => (
                <Select.Option key={type.id} value={type.title} name={type.title}>
                  {type.title}
                </Select.Option>
              ))}
          </Select>
        </td>
      </tr>

      {/* Vendor */}
      <tr>
        <td>{t("Vendor")}</td>

        <td>
          <Select
            onChange={(value: number, vendor: TProductVendor) =>
              setState(prev => ({ ...prev, vendor_id: value, vendor: vendor }))
            }
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("vendorSearch", query)}
            placeholder={t("Search vendors...")}
            searchValue={logistics.vendorSearch || ""}
            showSearch={true} // filters.vendorEnabled
            showDropDownOnFocus
            hideDropdown
            disabled={false} // !filters.vendorEnabled
            allowClear
          >
            {vendors
              ?.filter(val => val.title?.toLowerCase().includes(logistics.vendorSearch?.toLowerCase()))
              ?.map((vendor, index: number) => (
                <Select.Option key={index} value={vendor.id} name={vendor.title} extraValues={vendor}>
                  {vendor.title}
                </Select.Option>
              ))}
          </Select>
        </td>
      </tr>

      {/* Kitchen Location */}
      <tr>
        <td>{t("Kitchen location")}</td>

        <td>
          <Select
            onChange={(value: number) => setState(prev => ({ ...prev, kitchen_location_id: value }))}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("kitchenLocationSearch", query)}
            placeholder={t("Search kitchen locations...")}
            searchValue={logistics.kitchenLocationSearch || ""}
            showSearch={true} // filters.kitchenLocationEnabled
            allowClear
            showDropDownOnFocus
            hideDropdown
            disabled={false} // !filters.kitchenLocationEnabled
            defaultValue={state.kitchen_location_id}
          >
            <Select.Option value={-1} name={t("None")}>
              {t("None")}
            </Select.Option>
            {kitchenLocations?.map((location, index: number) => (
              <Select.Option key={index} value={location.id} name={location.title}>
                {location.title}
              </Select.Option>
            ))}
          </Select>
        </td>
      </tr>

      {/* Kitchen Location 2 */}
      <tr>
        <td>{t("Kitchen location 2")}</td>

        <td>
          <Select
            onChange={(value: number) => setState(prev => ({ ...prev, kitchen_location_2_id: value }))}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("kitchenLocation2Search", query)}
            placeholder={t("Search kitchen locations...")}
            searchValue={logistics.kitchenLocation2Search || ""}
            showSearch={true}
            showDropDownOnFocus
            hideDropdown
            disabled={false}
            defaultValue={state.kitchen_location_2_id}
            allowClear
          >
            <Select.Option value={-1} name={t("None")}>
              {t("None")}
            </Select.Option>
            {kitchenLocations?.map((location, index: number) => (
              <Select.Option key={index} value={location.id} name={location.title}>
                {location.title}
              </Select.Option>
            ))}
          </Select>
        </td>
      </tr>



      {/* Kitchen Location 3 */}
      <tr>
        <td>{t("Kitchen location 3")}</td>

        <td>
          <Select
            onChange={(value: number) => setState(prev => ({ ...prev, kitchen_location_3_id: value }))}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("kitchenLocation3Search", query)}
            placeholder={t("Search kitchen locations...")}
            searchValue={logistics.kitchenLocation3Search || ""}
            showSearch={true}
            showDropDownOnFocus
            hideDropdown
            disabled={false}
            defaultValue={state.kitchen_location_3_id}
            allowClear
          >
            <Select.Option value={-1} name={t("None")}>
              {t("None")}
            </Select.Option>
            {kitchenLocations?.map((location, index: number) => (
              <Select.Option key={index} value={location.id} name={location.title}>
                {location.title}
              </Select.Option>
            ))}
          </Select>
        </td>
      </tr>

      {/* Product Unit */}
      <tr>
        <td>{t("Product unit")}</td>

        <td>
          <Select
            onChange={(value: number) => setState(prev => ({ ...prev, product_unit_id: value }))}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("productUnitSearch", query)}
            placeholder={t("Search product units...")}
            searchValue={logistics.productUnitSearch || ""}
            showSearch={true}
            showDropDownOnFocus
            hideDropdown
            disabled={false}
            defaultValue={state.product_unit_id}
            allowClear
          >
            <Select.Option value={-1} name={t("None")}>
              {t("None")}
            </Select.Option>
            {productUnits?.map((unit, index: number) => (
              <Select.Option key={index} value={unit.id} name={unit.name}>
                {`${unit.name} (${unit.abbreviation})`}
              </Select.Option>
            ))}
          </Select>
        </td>
      </tr>

      {/* Customer Required */}
      <tr>
        <td>{t("Customer required")}</td>

        <td>
          <Toggle
            id="customer_required"
            checked={state.customer_required}
            onChange={() => setState(prev => ({ ...prev, customer_required: !prev.customer_required }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>

      {/* Shipping Required */}
      <tr>
        <td>{t("Shipping required")}</td>

        <td>
          <Toggle
            id="shipping_required"
            checked={state.shipping_required}
            onChange={() => setState(prev => ({ ...prev, shipping_required: !prev.shipping_required }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>

      {/* Fulfillment Required */}
      <tr>
        <td>{t("Fulfillment required")}</td>

        <td>
          <Toggle
            id="fulfillment_required"
            checked={state.fulfillment_required}
            onChange={() => setState(prev => ({ ...prev, fulfillment_required: !prev.fulfillment_required }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>

      {/* Track Inventory */}
      <tr>
        <td>{t("Track inventory")}</td>

        <td>
          <Toggle
            id="track_inventory"
            checked={state.track_inventory}
            onChange={() => setState(prev => ({ ...prev, track_inventory: !prev.track_inventory }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>
    </DefaultsTableLayout>
  );
}
